// =============================================================================
// io/sync/preferences.ts — the single `user_preferences` row.
// -----------------------------------------------------------------------------
// Settings is the second-largest surface in the old client (922 renderer lines,
// 26 inputs, 42 buttons) and every one of those controls writes here. So this
// module has one job beyond load and save: **make it impossible to overwrite a
// preference the user set from a screen that never saw it.**
//
// ── 🎯 WHY SAVES ARE PATCHES, NEVER WHOLE ROWS ──
//
// The obvious shape is `load() → edit in memory → upsert the whole object`. It
// has two failure modes, and both destroy real settings rather than merely
// failing:
//
// 1. **A failed load becomes a factory reset.** If the read errors and the UI
//    falls back to defaults — as almost every settings screen does — the first
//    save writes those defaults over everything the user had. One network blip
//    silently resets their timezone, their API key, their confluence list, their
//    mistake tags. The old client shipped exactly this shape for entitlement and
//    a single failed SELECT booted a paying customer as Free (D-log; the fix was
//    to distinguish "no row" from "could not read").
//
// 2. **Two tabs clobber each other.** Tab A loads, tab B changes the timezone,
//    tab A saves an hour later and reverts it. `trades` needed a whole
//    optimistic-concurrency guard for this (v2.72.0, per-row `updated_at`).
//    Preferences do not need one, because a patch only carries the fields that
//    actually changed — the conflict cannot arise for fields nobody touched.
//
// A patch closes both by construction rather than by discipline: there is no API
// here that can send a field the caller did not explicitly set.
//
// ⚠ There is deliberately no `savePreferences(prefs)` overload taking the whole
// object. It would be convenient and it is the exact call that reintroduces both
// failures above.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';

export type DayBoundaryMode = 'calendar' | 'session';
export type AutoBackupFreq = 'off' | 'daily' | 'weekly';

/**
 * The app-side shape. camelCase; the DB is snake_case and the mapping is the
 * only place that knows both.
 */
export interface Preferences {
  displayName: string;
  anthropicApiKey: string;
  webhooks: Record<string, string>;
  sessionTimezone: string;
  dayBoundaryMode: DayBoundaryMode;
  autoImport: boolean;
  autoImportIntervalSec: number;
  openPositionLiveTradeHours: number;
  autoBackup: AutoBackupFreq;
  autoBackupKeepLast: number;
  customMistakeTags: string[];
  confluences: unknown[];
  patternCategories: unknown[];
  readinessGroups: unknown[];
  hiddenTabs: string[];
  suppressedOpenPositions: unknown[];
  /**
   * The neutral per-user tag registry — `[{ id, name, color }]`.
   *
   * ⚠ Read so the trade form can RENDER a trade's applied tags. `Trade.tags`
   * holds ids only, so without the registry a trade that HAS tags renders "No
   * tags" — the D61 failure exactly: a blank the user then confirms.
   */
  customTags: unknown[];
  /**
   * `{ tagName: boolean }` — the mistake tags the user has opted OUT of counting
   * as controllable.
   *
   * ── ⚠ SPARSE, AND ABSENT MEANS **TRUE** ──
   *
   * It holds only the exclusions, so a tag with no entry still counts. Not
   * reading this column at all would be the same as an empty map, which is the
   * SAFE direction — but only until the user unticks something in Settings, at
   * which point Controllable Losses on the Day Overview would keep counting a
   * tag they explicitly excluded, and nothing on the screen would say why.
   */
  controllableMistakeTags: Record<string, unknown>;
  /**
   * The grab-bag of UI flags: `plannedTargetUnit`, `tradeFormLayout`, and others
   * this client does not read.
   *
   * ⚠ Read as a WHOLE OBJECT and written back the same way. It is one JSONB
   * column shared by flags from every surface, so a save that rebuilt it from the
   * keys this client knows would drop the ones it does not.
   */
  appFlags: Record<string, unknown>;
  /**
   * The trader's OWN saved rule templates, offered by the "Set up account" modal.
   *
   * ⚠ THESE ARE NOT FIRM RULES AND MUST NEVER BE PRESENTED AS ANY. Beta
   * deliberately REMOVED per-firm templates, and its reasoning is load-bearing:
   * "Stale per-firm rules are a CORRECTNESS risk, not a cosmetic one — they feed
   * `computeDrawdownFloor`, so a lagging copy hands the trader a wrong drawdown
   * buffer and they blow an account trusting our math." What replaced them is
   * this: numbers the USER saved from an account they had already set up
   * correctly, carrying a `savedAt` so an old one is visibly old.
   *
   * ⚠ Read as an opaque array here and parsed in `domain/accountSetup`. They are
   * per-user and NOT shareable — sharing would recreate the stale-rules problem
   * one step removed, with the added twist that the author would be someone else.
   */
  propTemplates: unknown[];
  /**
   * `{ MES: { riskPoints, rrPlanned } }` — the user's own risk methodology, per
   * instrument.
   *
   * ⚠ NOT a nicety. It is what the trade form seeds Risk (pts) and R:R Planned
   * from when the trade itself carries neither, and the old client is explicit
   * that there is "NO magic 4-pt default at the platform level" — the number has
   * to come from the trader.
   *
   * ⚠ Found by opening the form on Josh's real trade beside his screenshot of the
   * same one: beta showed Planned R 2.00 and Realized R 3.42 where the port showed
   * em dashes. The trade has no stored risk at all — beta was filling the field
   * from this setting, and the screenshot could not distinguish the two.
   */
  defaultRiskByInstrument: Record<string, unknown>;
}

/** A save carries only what changed. See the header. */
export type PreferencesPatch = Partial<Preferences>;

// ── Column mapping ───────────────────────────────────────────────────────────
// ⚠ ONE table, derived in both directions, so a field cannot be readable and
// unwritable (or worse, writable under a misspelled column that Postgres
// accepts into `extras` and nothing ever reads back).
const COLUMNS = {
  displayName: 'display_name',
  anthropicApiKey: 'anthropic_api_key',
  webhooks: 'webhooks',
  sessionTimezone: 'session_timezone',
  dayBoundaryMode: 'day_boundary_mode',
  autoImport: 'auto_import',
  autoImportIntervalSec: 'auto_import_interval_sec',
  openPositionLiveTradeHours: 'open_position_live_trade_hours',
  autoBackup: 'auto_backup',
  autoBackupKeepLast: 'auto_backup_keep_last',
  customMistakeTags: 'custom_mistake_tags',
  confluences: 'confluences',
  patternCategories: 'pattern_categories',
  readinessGroups: 'readiness_groups',
  hiddenTabs: 'hidden_tabs',
  suppressedOpenPositions: 'suppressed_open_positions',
  customTags: 'custom_tags',
  controllableMistakeTags: 'controllable_mistake_tags',
  appFlags: 'app_flags',
  defaultRiskByInstrument: 'default_risk_by_instrument',
  propTemplates: 'prop_templates',
} as const satisfies Record<keyof Preferences, string>;

export const PREFERENCE_KEYS = Object.keys(COLUMNS) as (keyof Preferences)[];
export const PREFERENCES_SELECT = Object.values(COLUMNS).join(',');

/**
 * The defaults, matching the DDL.
 *
 * ⚠ These are what a user with NO ROW gets. They are never a fallback for a
 * FAILED read — `loadPreferences` throws in that case rather than returning
 * these, because the caller cannot tell the two apart and would save them back.
 */
export const DEFAULT_PREFERENCES: Preferences = {
  displayName: '',
  anthropicApiKey: '',
  webhooks: { preMarketPlan: '', tradeDetails: '' },
  sessionTimezone: 'America/New_York',
  dayBoundaryMode: 'calendar',
  autoImport: false,
  autoImportIntervalSec: 30,
  openPositionLiveTradeHours: 4,
  autoBackup: 'off',
  autoBackupKeepLast: 10,
  customMistakeTags: ['hesitation', 'chased', 'moved-stop', 'sized-too-big', 'late-entry', 'early-exit'],
  confluences: [],
  patternCategories: [],
  readinessGroups: [],
  hiddenTabs: [],
  suppressedOpenPositions: [],
  customTags: [],
  controllableMistakeTags: {},
  appFlags: {},
  defaultRiskByInstrument: {},
  propTemplates: [],
};

export function preferencesFromDb(row: Record<string, unknown>): Preferences {
  const out = { ...DEFAULT_PREFERENCES };
  for (const key of PREFERENCE_KEYS) {
    const v = row[COLUMNS[key]];
    // ⚠ null-coalesce, not `||`. `autoImport: false`, `autoBackupKeepLast: 0`
    // and `displayName: ''` are all real stored values that `||` would silently
    // replace with the default — a user who cleared their display name would
    // find it back on the next load.
    if (v !== undefined && v !== null) (out as Record<string, unknown>)[key] = v;
  }
  return out;
}

export function patchToDb(patch: PreferencesPatch): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  for (const key of PREFERENCE_KEYS) {
    // `in`, not truthiness: `{ autoImport: false }` is a real instruction to
    // turn it off, and a truthiness check would drop it.
    if (key in patch) row[COLUMNS[key]] = patch[key];
  }
  return row;
}

export type LoadPreferencesResult = {
  prefs: Preferences;
  /** False when the user has no row yet — a NEW user, not a failed read. */
  exists: boolean;
};

/**
 * Read the row.
 *
 * Throws on a read error. Returns defaults with `exists: false` only when the
 * query SUCCEEDED and found nothing — the distinction the caller needs to avoid
 * treating an outage as a new account.
 */
export async function loadPreferences(sb: SupabaseClient, userId: string): Promise<LoadPreferencesResult> {
  const { data, error } = await sb
    .from('user_preferences')
    .select(PREFERENCES_SELECT)
    .eq('user_id', userId)
    .maybeSingle();

  if (error) throw new Error(`preferences read failed: ${error.message}`);
  if (!data) return { prefs: { ...DEFAULT_PREFERENCES }, exists: false };
  // ⚠ Double cast, deliberately. PREFERENCES_SELECT is computed at runtime from
  // the column map, so supabase-js cannot infer a row shape from it and types
  // `data` as GenericStringError. Hand-writing the select string as a literal
  // would restore inference — and reintroduce exactly the drift the derived map
  // exists to prevent, where a column is writable but never read back.
  return { prefs: preferencesFromDb(data as unknown as Record<string, unknown>), exists: true };
}

/**
 * Write ONLY the fields in `patch`.
 *
 * Uses upsert on the primary key so a user whose row was never created (the
 * signup hook is the normal creator, and it can fail) still gets one — but the
 * inserted row carries only the patched columns plus the DDL defaults for the
 * rest, never a client-side snapshot.
 *
 * An empty patch is a no-op rather than an error: a Settings screen that saves
 * on blur will call this with nothing changed, and making that a failure would
 * turn a non-event into an error toast.
 */
export async function savePreferences(
  sb: SupabaseClient,
  userId: string,
  patch: PreferencesPatch,
): Promise<{ written: string[] }> {
  const row = patchToDb(patch);
  const written = Object.keys(row);
  if (!written.length) return { written };

  const { error } = await sb
    .from('user_preferences')
    .upsert({ user_id: userId, ...row }, { onConflict: 'user_id' });

  if (error) throw new Error(`preferences save failed: ${error.message}`);
  return { written };
}
